import type { TokenOptions } from '@/api/client'

/**
 * Builds the function the HTTP client calls to obtain the access token.
 *
 * The identity library is injected so the retry/redirect rules can be
 * tested without MSAL:
 *   - no account            -> null (the router guard sends the user to sign in)
 *   - silent acquisition OK -> the token (MSAL serves the cache and renews it)
 *   - interaction required  -> start a redirect once and return null
 *   - any other failure     -> rethrown (network etc.), never swallowed
 */
export interface TokenProviderDeps {
  hasAccount: () => boolean
  acquireTokenSilent: (options: { forceRefresh: boolean }) => Promise<{ accessToken: string }>
  acquireTokenRedirect: () => Promise<void>
  isInteractionRequired: (error: unknown) => boolean
}

export function createTokenProvider(deps: TokenProviderDeps) {
  // Several requests can fail at once; only the first may start the redirect.
  let redirecting = false

  return async (options?: TokenOptions): Promise<string | null> => {
    if (redirecting || !deps.hasAccount()) return null

    try {
      const result = await deps.acquireTokenSilent({ forceRefresh: options?.forceRefresh ?? false })
      return result.accessToken || null
    } catch (error) {
      if (!deps.isInteractionRequired(error)) throw error
      redirecting = true
      try {
        await deps.acquireTokenRedirect()
      } catch (redirectError) {
        redirecting = false
        throw redirectError
      }
      return null
    }
  }
}
